import { useState } from 'react';
import { Link, NavLink, Outlet } from 'react-router';
import {
    MdOutlineDashboard,
    MdOutlineSettings,
    MdOutlineSearch,
    MdOutlineNotifications,
    MdOutlineTune,
    MdOutlineMenu,
    MdClose,
    MdOutlineLocationOn,
    MdOutlineLogout,
} from 'react-icons/md';
import { SidebarExplorer, FormSelect } from '@podus/ui';
import { useAuth } from '@/context/AuthContext';
import { useMultidoorState } from '@/context/StateContext';

const NAV_ITEMS = [
    { label: 'Dashboard', icon: MdOutlineDashboard, to: '/dashboard' },
    { label: 'Settings', icon: MdOutlineSettings, to: '/dashboard/settings' },
];

export default function UserDashboardLayout() {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const { logout } = useAuth();
    const { states, loading: statesLoading, selectedState, selectStateBySlug } = useMultidoorState();

    return (
        <div className="flex h-dvh bg-[#fafafa]">
            {/* Mobile overlay */}
            {sidebarOpen && (
                <div className="fixed inset-0 z-30 bg-black/30 lg:hidden" onClick={() => setSidebarOpen(false)} />
            )}

            {/* ── Sidebar ── */}
            <aside
                className={`fixed inset-y-0 left-0 z-40 flex w-64 flex-col border-r border-[#E0E0E0] bg-white transition-transform lg:static lg:translate-x-0 ${
                    sidebarOpen ? 'translate-x-0' : '-translate-x-full'
                }`}
            >
                {/* Logo */}
                <div className="flex h-16 items-center border-b border-[#E0E0E0] px-5">
                    <img src="/logo/multi-door.svg" alt="Podus Multidoor" className="h-8" />
                </div>

                {/* Nav */}
                <nav className="flex-1 overflow-y-auto px-3 py-4 mt-2">
                    {NAV_ITEMS.map((item) => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            end={item.to === '/dashboard'}
                            onClick={() => setSidebarOpen(false)}
                            className={({ isActive }) =>
                                `flex items-center gap-3 rounded-lg px-3 py-2.5 mb-2 text-[14px] transition-colors leading-6 ${
                                    isActive ? 'font-extrabold text-secondary' : 'text-[#4E4D4D] hover:bg-[#f5f5f5]'
                                }`
                            }
                        >
                            {({ isActive }) => (
                                <>
                                    <item.icon size={20} className={isActive ? 'text-primary' : ''} />
                                    {item.label}
                                </>
                            )}
                        </NavLink>
                    ))}
                </nav>

                {/* Explore section at bottom */}
                <div className="mt-auto border-t border-[#eee] px-3 pt-4">
                    <SidebarExplorer currentApp="podus-multidoor" />
                </div>
            </aside>

            {/* ── Main area ── */}
            <div className="flex flex-1 flex-col overflow-y-auto">
                {/* Header */}
                <header className="sticky top-0 z-20 flex h-16 shrink-0 items-center gap-4 border-b border-[#E0E0E0] bg-white/80 px-4 backdrop-blur-md lg:px-8">
                    {/* Mobile hamburger */}
                    <button type="button" title="Open menu" onClick={() => setSidebarOpen(true)} className="lg:hidden">
                        <MdOutlineMenu size={24} className="text-secondary" />
                    </button>

                    {/* State selector */}
                    <div className="items-center gap-2 flex">
                        <div className="w-48">
                            <FormSelect
                                value={selectedState?.slug ?? ''}
                                onChange={(v) => selectStateBySlug(v)}
                                placeholder={
                                    statesLoading
                                        ? 'Loading…'
                                        : states.length === 0
                                          ? 'No states available'
                                          : 'Select state'
                                }
                                disabled={statesLoading || states.length === 0}
                                options={states.map((s) => ({ label: s.name, value: s.slug }))}
                                className="py-2! text-[13px]!"
                            />
                        </div>
                    </div>

                    <div className="flex-1" />
                    <Link
                        to="/dashboard/settings"
                        title="Profile"
                        className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary text-sm font-bold text-white transition-opacity hover:opacity-90"
                    >
                        U
                    </Link>
                    <button
                        type="button"
                        title="Logout"
                        onClick={() => logout()}
                        className="flex h-9 w-9 items-center justify-center rounded-lg text-[#555] transition-colors hover:bg-[#f5f5f5]"
                    >
                        <MdOutlineLogout size={20} />
                    </button>
                </header>

                {/* Page content */}
                <main className="flex-1 px-4 py-6 lg:px-8">
                    <Outlet />
                </main>
            </div>

            {/* Mobile close button */}
            {sidebarOpen && (
                <button
                    type="button"
                    title="Close sidebar"
                    onClick={() => setSidebarOpen(false)}
                    className="fixed left-64 top-4 z-50 flex h-8 w-8 items-center justify-center rounded-full bg-white shadow-md lg:hidden"
                >
                    <MdClose size={18} />
                </button>
            )}
        </div>
    );
}
